import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { cn } from '../lib/cn'

export type ToastTone = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  tone: ToastTone
  message: string
  /** 0 keeps it on screen until dismissed — used for connection loss. */
  durationMs: number
}

const ICONS = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
} as const

const TONES: Record<ToastTone, string> = {
  success: 'border-success/40 bg-success/10 text-success',
  error: 'border-momo-red/40 bg-momo-red/10 text-momo-red',
  warning: 'border-momo-yellow/40 bg-momo-yellow/10 text-momo-yellow',
  info: 'border-info/40 bg-info/10 text-info',
}

interface ToastContextValue {
  toasts: Toast[]
  show: (message: string, options?: { tone?: ToastTone; durationMs?: number }) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const show = useCallback<ToastContextValue['show']>((message, options = {}) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    setToasts((current) => [
      ...current,
      { id, message, tone: options.tone ?? 'info', durationMs: options.durationMs ?? 4000 },
    ])
    return id
  }, [])

  const value = useMemo(() => ({ toasts, show, dismiss }), [toasts, show, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside a <ToastProvider>')
  return context
}

export function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[]
  onDismiss: (id: string) => void
}) {
  return (
    <section
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </section>
  )
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const Icon = ICONS[toast.tone]

  useEffect(() => {
    if (toast.durationMs <= 0) return
    const timer = setTimeout(() => onDismiss(toast.id), toast.durationMs)
    return () => clearTimeout(timer)
  }, [toast.id, toast.durationMs, onDismiss])

  return (
    <output
      aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
      className={cn(
        'pointer-events-auto flex w-full max-w-md animate-slide-up items-center gap-3 rounded-xl border px-4 py-3 shadow-card backdrop-blur-sm',
        TONES[toast.tone],
      )}
    >
      <Icon aria-hidden="true" className="size-5 shrink-0" />
      <span className="flex-1 text-content text-sm">{toast.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss"
        className="shrink-0 text-content-muted transition-colors hover:text-content"
      >
        ✕
      </button>
    </output>
  )
}
